<script lang="ts">
	import { DataHandlerUserAdmin, type User } from "luxedo-data"
	import { LuxedoRPC } from "luxedo-rpc"
	import { Toast } from "toaster"
	import { openConfirmOverlay } from "overlay"
	import { TrashIcon } from "luxedo-icons"
	import { navigate } from "svelte-routing"
	import UserPrivs from "./UserPrivs.svelte"

	interface Props {
		user: User
	}

	let { user }: Props = $props()

	async function deleteUser() {
		const onVerify = async () => {
			try {
				navigate("/admin/user")
				await DataHandlerUserAdmin.deleteEntry(user)
				Toast.success("Account successfully deleted!")
			} catch (e) {
				Toast.error("Unable to delete user.")
				console.error("Unable to delete user.", e)
			}
		}

		openConfirmOverlay(
			{
				invertWarning: true,
				prompt: `Are you sure you want to delete this user?`,
				buttons: {
					confirm: {
						text: "Yes",
						onClick: onVerify,
					},
				},
			},
			{
				heading: "Delete User",
			}
		)
	}

	async function loginAsUser() {
		try {
			await LuxedoRPC.api.admin.login_as(user.email)
			Toast.success(`Logged in as ${user.firstname} ${user.lastname}.`)
		} catch (e) {
			Toast.error(`Unable to login as ${user.firstname} ${user.lastname}.`)
			console.error("Error loggining in as user.", e)
		}
	}

	async function toggleTrial() {
		try {
			await LuxedoRPC.api.admin.account_set_trial(user.id, user.is_demo ? 0 : 1)
			await DataHandlerUserAdmin.pull([user.id])
			Toast.success("Trial status updated.")
		} catch (e) {
			Toast.error("Unable to update trial status.")
			console.error(`Unable to update trial status for user #${user.id}.`, e)
		}
	}
</script>

<div class="info">
	<h2>User Info</h2>
	<div class="info-wrapper">
		<span class="info-title">ID: </span>
		<span>
			{user?.id}
		</span>
	</div>
	<div class="info-wrapper">
		<span class="info-title">Name: </span>
		<span>
			{`${user?.firstname} ${user?.lastname}`}
		</span>
	</div>
	<div class="info-wrapper">
		<span class="info-title">Email: </span>
		<a href="mailto:{user?.email}">{user?.email}</a>
	</div>
	<div class="info-wrapper">
		<span class="info-title">Actions: </span>
		<span class="flex-row action-buttons">
			<button class="small" title="Login as this User" onclick={loginAsUser}
				>Login As User</button
			>
			<button
				class="small only-icon cancel"
				title="Delete User"
				onclick={deleteUser}><TrashIcon /></button
			>
		</span>
	</div>
	<div class="info-wrapper">
		<span class="info-title">Trial: </span>
		<span
			class={`flex-row center-ver trial-row ${user?.is_demo ? "success" : "alert"}`}
		>
			{#if user?.is_demo}
				Enabled
			{:else}
				Disabled
			{/if}
			<button
				class={`small ${user.is_demo ? "cancel" : ""}`}
				onclick={toggleTrial}
			>
				{#if !user.is_demo}
					Enable
				{:else}
					Disable
				{/if}
			</button>
		</span>
	</div>
	<div class="info-wrapper">
		<span class="info-title"> Privileges: </span>
		<UserPrivs {user} />
	</div>
</div>

<style>
	.action-buttons {
		height: 2rem;
	}

	.action-buttons button {
		margin: 0 0.25rem;
	}

	.action-buttons button :global(svg) {
		max-height: 1rem;
		max-width: 1rem;
		height: 1rem;
		width: auto;
	}

	.trial-row button {
		margin-left: 1rem;
	}
</style>
